import {useState, useContext} from 'react';
import './CompaniesPage.css';

import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { companyRenderFields, industryDetails } from '../../constants.jsx';
import { renderFormRow, genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';


async function addCompanyToDatabase(company) {
  return genericPOSTDatabaseCall('/api/fb/addCompany', { company }, 'company');
}

async function saveCompanyToDatabase(company) {
    return genericPOSTDatabaseCall('/api/fb/saveCompanyInfo', { company }, 'company info');
}

async function deleteCompanyFromDatabase(companyId) {
    return genericDELETEDatabaseCall('/api/fb/deleteCompany', { companyId }, 'company');
}

export function CompanyDetails({ company, setCompanies, messageApi, editable = true, setCompanySelectedID, spotRates }) {
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const saveCompany = async (company) => {
        setSaving(true);
        messageApi.loading('Saving company info...', 0);
        try {
            await saveCompanyToDatabase(company);
            messageApi.destroy();
            messageApi.success('Company info saved successfully');
        } catch (error) {
            console.error('Error saving company info:', error);
            messageApi.destroy();
            messageApi.error('Failed to save company info');
        }
        setSaving(false);
    }

    const deleteCompany = async (companyId) => {
        setDeleting(true);
        messageApi.loading('Deleting company...', 0);
        try {
            await deleteCompanyFromDatabase(companyId);
            setCompanies(prevCompanies => prevCompanies.filter(comp => comp.id !== companyId));
            setCompanySelectedID(""); // Clear selected company if it was deleted
            messageApi.destroy();
            messageApi.success('Company deleted successfully');
        } catch (error) {
            console.error('Error deleting company:', error);
            messageApi.destroy();
            messageApi.error('Failed to delete company');
        }
        setDeleting(false);
    }

    const handleInputChange = (event, changedCompany, key, field) => {
        let newValue = event.target.value;
        const updatedCompanyBase = { ...changedCompany, [key]: newValue };

        if (key === 'industry') {
            const keys = Object.keys(industryDetails[newValue]);
            var newProducts = [ ...updatedCompanyBase.products ];
            for (var i = 0; i < newProducts.length; i++) {
                newProducts[i] = { ...newProducts[i], "growth_rate": industryDetails[newValue]['default_growth_rate'], "growth_adjustment": industryDetails[newValue]['default_growth_adjustment']};
            }
                
            updatedCompanyBase.products = newProducts;

            for (const k of keys) {                
                updatedCompanyBase[k] = industryDetails[newValue][k];
            }
        }

        if (key === 'in_currency' || key === 'out_currency') {
            // Update spot rate based on currency change
            const conversionKey = updatedCompanyBase.in_currency + '_' + updatedCompanyBase.out_currency;
            const value = spotRates[conversionKey] || '1.000';

            updatedCompanyBase.spot_rate = value;
        }

        setCompanies(prevCompanies => {
            return prevCompanies.map(company => {
                return company.id === updatedCompanyBase.id ? updatedCompanyBase : company;
            });
        });
    }

    const handleInputBlur = (event, changedCompany, key, field) => {
        let newValue = event.target.value;

        if (field.regexCondition && field.errorMessage && !field.regexCondition.test(newValue)) {
            messageApi.error(field.errorMessage);
            newValue = field.default || ''; // Use default value if provided
        }

        if (newValue === '') {
            messageApi.error('This field cannot be empty');
            newValue = field.default || ''; // Use default value if provided
        }

        setCompanies(prevCompanies => {
            return prevCompanies.map(company => {
                if (company.id === changedCompany.id) {
                    return { ...company, [key]: newValue };
                }
                return company;
            });
        });
    }

    return (
        <div key={company.id} className="entity-card">
            {companyRenderFields.map(field => renderFormRow(field, company, handleInputChange, editable, handleInputBlur))}
            <div className="form-row">
                <label htmlFor={"spot_rate_" + company.id}>Spot Rate</label>
                <input id={"spot_rate_" + company.id} type="text" value={company.spot_rate} className="form-input" readOnly></input>
            </div>
            {!editable ? <></> : (
            <div className="flex-row bottom-actions">
                <div className="form-row actions-row">
                    <button type='button' disabled={deleting} onClick={() => deleteCompany(company.id)} className="delete-button">Delete Company</button>
                </div>
                <div className="form-row actions-row">
                    <button type='button' disabled={saving} onClick={() => saveCompany(company)} className="save-button">Save Company</button>
                </div>
            </div>
            )}
        </div>
    )
}

function CompaniesPage({contextToUse}) {
    const { companies: contextCompanies, setCompanies, setPageNum, messageApi, companySelectedID, setCompanySelectedID, spotRates } = useContext(contextToUse || FinancialBuilderContext);

    const [newCompanyName, setNewCompanyName] = useState('');
    
    
    const addCompany = async () => {
        if (!newCompanyName) {
            messageApi.error('Please enter a company name');
            return;
        }
        messageApi.loading('Adding company...', 0);
        try {
            const newCompany = await addCompanyToDatabase(newCompanyName);

            // Update company stuff before adding to state
            const keys = Object.keys(industryDetails[newCompany.industry]);
            var newProducts = [ ...newCompany.products ];
            for (var i = 0; i < newProducts.length; i++) {
                newProducts[i] = { ...newProducts[i], "growth_rate": industryDetails[newCompany.industry]['default_growth_rate'], "growth_adjustment": industryDetails[newCompany.industry]['default_growth_adjustment']};
            }
            newCompany.products = newProducts;
            for (const k of keys) {
                newCompany[k] = industryDetails[newCompany.industry][k];
            }

            setCompanies(prevCompanies => [...prevCompanies, newCompany]);
            setNewCompanyName(''); // Clear input field

            messageApi.destroy();
            messageApi.success('Company added successfully');
        } catch (error) {
            console.error('Error adding company:', error);
            messageApi.destroy();
            messageApi.error('Failed to add company');
        }
    }

    return (
        <div className="financial-builder-container entity-industry-container">
            <h2>All Companies</h2>
            <div>
                {contextCompanies.map(company => (
                    <CompanyDetails key={company.id} spotRates={spotRates} setCompanySelectedID={setCompanySelectedID} messageApi={messageApi} setCompanies={setCompanies} company={company} />
                ))}
            </div>
            <div className="add-entity-controls form-section">
                <div className="form-row">
                    <label htmlFor="new_company_name">Add New Company</label>
                    <input type="text" className="form-input" value={newCompanyName} onChange={(e) => setNewCompanyName(e.target.value)} placeholder="Enter company name" id="new_company_name" />
                </div>
                <button className="add-button" onClick={addCompany}>ADD Company</button>
            </div>
            <div className="form-section">
                <div className="form-row">
                    <label htmlFor="select_company_for_next">Select Company</label>
                    <select id="select_company_for_next" className="form-input" value={companySelectedID || ''} onChange={(e) => setCompanySelectedID(e.target.value)}>
                        <option value="" disabled hidden>-- Select a company --</option>
                        {contextCompanies.map(company => (
                            <option key={company.id} value={company.id}>{company.name}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="navigation-buttons">
                <button type="button" disabled={companySelectedID === "" || contextCompanies.length === 0} onClick={() => setPageNum(2)} className="next-button">Next</button>
            </div>
        </div>
    );
}

export default CompaniesPage;